import { Graph, Node, Link, LinkStrength, SatelliteType, AnchorType, Anchor, Spawn } from "@livereader/graphly-d3";
import { useDomainStore } from "../../../store/domain";
import { useInstanceWorkspacesStore } from "../../../store/i-workspace";
import { useGraphStore } from "../../../store/graph";
import { useGraphlyTemplatesStore } from "../../../store/graphly-templates";
import { useSymbolsStore } from "../../../store/symbols";
import {
	ConceptAttribute,
	ConceptDataobject,
	DescriptorNotification,
	InstanceAttribute,
	InstanceDataobject,
	InstanceWorkspace,
	LanguageCode,
	TemplateDataobject,
} from "@lr-notitia/notitia-operator/schemas";

let domainStore: ReturnType<typeof useDomainStore>;
let instanceWorkspacesStore: ReturnType<typeof useInstanceWorkspacesStore>;
let graphStore: ReturnType<typeof useGraphStore>;
let graphlyTemplatesStore: ReturnType<typeof useGraphlyTemplatesStore>;
let symbolsStore: ReturnType<typeof useSymbolsStore>;
function init() {
	domainStore = useDomainStore();
	instanceWorkspacesStore = useInstanceWorkspacesStore();
	graphStore = useGraphStore();
	graphlyTemplatesStore = useGraphlyTemplatesStore();
	symbolsStore = useSymbolsStore();
}

export function graph(workspaceId: string, selectedNodeId: string): Graph {
	if (!instanceWorkspacesStore || !graphStore) init();
	const workspace = instanceWorkspacesStore.getById(workspaceId);
	if (!workspace) return { nodes: [], links: [] };
	const nodes: Node[] = [];
	const links: Link[] = [];

	// dataobject nodes
	const dataobjects = workspace.dataobjects;
	dataobjects
		.map((d) => graphStore.instance.dataobject.byId(d))
		.filter((d) => d !== undefined)
		.forEach((dataobject) => {
			if (!dataobject) return;

			// if hidden in designer, dont show here
			let templateDataobject = graphStore.template.dataobject.byId(dataobject?.template || "");
			if (templateDataobject?.context?.["nta-hidden"]) return;

			let conceptDataobject = graphStore.concept.dataobject.byId(dataobject?.concept || "");
			if (!conceptDataobject) return;
			if (!nodes.find((n) => n.id === dataobject?.id)) {
				nodes.push(node(dataobject, conceptDataobject, workspace, nodeType(conceptDataobject)));
			}
		});

	// hasA
	function buildHasADataobjects(dataobject: InstanceDataobject, workspace: InstanceWorkspace) {
		dataobject.hasA.forEach((hasAId) => {
			let hasADataobject = graphStore.instance.dataobject.byId(hasAId);
			if (!hasADataobject) return;

			// if not modeled in designer (== template does not exists), dont show here
			let template = graphStore.template.dataobject.byId(hasADataobject?.template || "");
			if (!template) return;

			// if hidden in designer, dont show here
			if (template?.context?.["nta-hidden"]) return;

			let concept = graphStore.concept.dataobject.byId(hasADataobject?.concept || "");
			if (!concept) return;
			if (!nodes.find((n) => n.id === hasADataobject?.id)) {
				nodes.push(node(hasADataobject, concept, workspace, nodeType(concept)));
			}
			links.push({
				id: `${dataobject?.id}_${hasAId}`,
				curvature: 0.05,
				strength: LinkStrength.Loose,
				directed: true,
				source: dataobject?.id || "",
				target: hasAId,
			});
			buildHasADataobjects(hasADataobject, workspace);
		});
	}
	dataobjects
		.map((d) => graphStore.instance.dataobject.byId(d))
		.filter((d) => d !== undefined)
		.forEach((dataobject) => {
			buildHasADataobjects(dataobject as InstanceDataobject, workspace);
		});

	// commented out since this feature was considered more confusing than useful for now - jnhck 15.10.2025
	// selected node satellites
	// if (selectedNodeId) {
	// 	let allInstantiable = getInstantiableDataobjects(selectedNodeId);
	// 	if (allInstantiable.length != 0) {
	// 		let angle = 180;
	// 		let step = allInstantiable.length >= 12 ? 360 / allInstantiable.length : 30;
	// 		allInstantiable.forEach((instantiable) => {
	// 			let templateDataobject = graphStore.template.dataobject.byId(instantiable.templateId);
	// 			if (!templateDataobject) return;
	// 			let conceptDataobject = graphStore.concept.dataobject.byId(templateDataobject?.concept || "");
	// 			if (!conceptDataobject) return;
	// 			let satellite = suggestionNode(
	// 				selectedNodeId,
	// 				templateDataobject,
	// 				conceptDataobject,
	// 				nodeType(conceptDataobject),
	// 				angle,
	// 				instantiable.preventInstantiation,
	// 				instantiable.parentInstanceId,
	// 				nodes
	// 			);
	// 			if (satellite) {
	// 				if (!nodes.find((n) => n.id === satellite?.id)) nodes.push(satellite);
	// 				angle += step;
	// 			}
	// 		});
	// 	}
	// }

	// Satellite Notifications
	const satelliteNotifications = graphStore.descriptor.notifications.filter((n) => n.display === "satellite");
	satelliteNotifications.forEach((notification, i) => {
		if (!notification.regarding) return;
		let satellite = satelliteNotificationNode(notification, 60 + 15 * i, nodes);
		if (satellite && !nodes.find((n) => n.id === satellite?.id)) nodes.push(satellite);
	});

	return { nodes, links };
}

function node(
	dataobject: InstanceDataobject,
	conceptDataobject: ConceptDataobject,
	workspace: InstanceWorkspace,
	type: string,
): Node {
	if (!graphStore || !domainStore) init();
	return {
		id: dataobject.id,
		...position(dataobject, workspace),
		shape: {
			type: conceptDataobject.graphlyTemplate ? type : "demo-hexagon",
			scale: scale(dataobject, workspace, 1),
			url: conceptDataobject.graphlyTemplate || graphlyTemplatesStore.demoHexagonShape,
		},
		payload: buildPayload(dataobject, conceptDataobject),
	};
}

function buildPayload(dataobject: InstanceDataobject, conceptDataobject: ConceptDataobject) {
	let payload: { [key: string]: any } = {};
	for (let key in conceptDataobject.graphlyPayloadMap) {
		let val = conceptDataobject.graphlyPayloadMap[key];
		if (val) {
			let composedIds = graphStore.instance.dataobject.composedDataobjectsIds(dataobject.id);
			let instanceValue = evaluate(dataobject, key, val, composedIds);
			if (
				instanceValue === undefined ||
				instanceValue === null ||
				instanceValue === "undefined" ||
				instanceValue === "unknown"
			)
				continue;
			payload[key] = instanceValue;
		}
		// else payload[key] = "undefined";
	}
	return payload;
}
function evaluate(dataobject: InstanceDataobject, key: string, val: any, composedIds: string[] | undefined) {
	if (!symbolsStore) init();
	// if nested object
	if (
		typeof val === "object" &&
		val.default == undefined &&
		val.value == undefined &&
		!Array.isArray(val) &&
		!isTerminologyObject(val)
	) {
		let payload: { [key: string]: any } = {};
		for (let childKey in val) {
			let childVal = val[childKey];
			if (childVal) payload[childKey] = evaluate(dataobject, childKey, childVal, composedIds);
		}
		return payload;
	}
	// attribute array
	else if (Array.isArray(val.value)) {
		let values: any[] = [];
		if (val.value.length === 0) return defaultValue(val.default);
		val.value.forEach((conceptAttributeId: string) => {
			let conceptAttribute = graphStore.concept.attribute.byId(conceptAttributeId);
			if (!conceptAttribute) return;
			let instanceAttribute = graphStore.instance.attributes.find(
				(a) => a.concept === conceptAttribute?.id && isPartOf(a, dataobject, composedIds),
			);
			if (!instanceAttribute) return;
			let value;
			value = attrValue(instanceAttribute.value);
			if (value === "undefined") return;
			// array-valued instance attribute (e.g. array<string> or array<enum>): spread items
			// individually, resolving each through attrValue so enum UUIDs become localized terms.
			// Skip the tags formatting for these.
			if (Array.isArray(value)) {
				for (const rawItem of value) {
					const item = attrValue(rawItem, key === "tags" ? domainStore.languagePreference : "en");
					if (item === undefined || item === null || item === "undefined") continue;
					values.push(item);
				}
				return;
			}
			// tags: concept term + ": " + value in preferred language
			if (key === "tags") {
				value = attrValue(instanceAttribute.value, domainStore.languagePreference);
				let conceptTerm =
					symbolsStore.term({
						id: conceptAttribute.refersTo,
						terminology: conceptAttribute.terminology,
						lang: domainStore.languagePreference,
					}) ||
					symbolsStore.term({
						id: conceptAttribute.refersTo,
						terminology: conceptAttribute.terminology,
						lang: "en",
					});
				value = conceptTerm + ": " + String(value);
			}
			values.push(value);
		});
		if (values.length === 0) return defaultValue(val.default);
		return values;
	}
	// case "old" payload map leftover array
	else if (Array.isArray(val)) {
		let values: any[] = [];
		val.forEach((conceptAttributeId: string) => {
			let conceptAttribute = graphStore.concept.attribute.byId(conceptAttributeId);
			if (!conceptAttribute) return;
			let instanceAttribute = graphStore.instance.attributes.find(
				(a) => a.concept === conceptAttribute?.id && isPartOf(a, dataobject, composedIds),
			);
			if (!instanceAttribute) return;
			let value;
			value = attrValue(instanceAttribute.value);
			if (value === "undefined") return;
			// array-valued instance attribute (e.g. array<string> or array<enum>): spread items
			// individually, resolving each through attrValue so enum UUIDs become localized terms.
			// Skip the tags formatting for these.
			if (Array.isArray(value)) {
				for (const rawItem of value) {
					const item = attrValue(rawItem, key === "tags" ? domainStore.languagePreference : "en");
					if (item === undefined || item === null || item === "undefined") continue;
					values.push(item);
				}
				return;
			}
			// tags: concept term + ": " + value in preferred language
			if (key === "tags") {
				value = attrValue(instanceAttribute.value, domainStore.languagePreference);
				let conceptTerm =
					symbolsStore.term({
						id: conceptAttribute.refersTo,
						terminology: conceptAttribute.terminology,
						lang: domainStore.languagePreference,
					}) ||
					symbolsStore.term({
						id: conceptAttribute.refersTo,
						terminology: conceptAttribute.terminology,
						lang: "en",
					});
				value = conceptTerm + ": " + String(value);
			}
			values.push(value);
		});
		return values;
	}
	// single attribute
	else {
		let conceptAttribute = graphStore.concept.attribute.byId(val.value);
		if (!conceptAttribute) return defaultValue(val.default);
		let instanceAttribute = graphStore.instance.attributes.find(
			(a) => a.concept === conceptAttribute?.id && isPartOf(a, dataobject, composedIds),
		);
		if (!instanceAttribute) return defaultValue(val.default);
		if (instanceAttribute.value === "undefined") return attrValue(defaultValue(val.default));
		let attrVal = attrValue(instanceAttribute.value);
		return attrVal && attrVal !== "unknown" && attrVal !== "undefined" ? attrVal : defaultValue(val.default);
	}
}
function isPartOf(attribute: InstanceAttribute, dataobject: InstanceDataobject, composedIds: string[] | undefined) {
	if (attribute.partOf === dataobject.id) return true;
	if (composedIds && composedIds.some((composedId) => attribute.partOf === composedId)) return true;
	return false;
}
function attrValue(value: any, lang: LanguageCode = "en"): boolean | string | object | null {
	if (!symbolsStore) init();
	let enumItem = graphStore.descriptor.enumItem.byId(value);
	// check if attribute is datatype of enumeration item boolean true
	if (enumItem && enumItem.refersTo === "00000000-0000-0000-0000-000000000101") return true;
	// check if attribute is datatype of enumeration item boolean false
	else if (enumItem && enumItem.refersTo === "00000000-0000-0000-0000-000000000102") return false;
	// other enum item
	else if (enumItem)
		return symbolsStore.term({
			id: enumItem.refersTo,
			terminology: enumItem?.terminology,
			lang: lang,
		});
	// no enum item
	return value || null;
}

function suggestionNode(
	selectedNodeId: string,
	templateDataobject: TemplateDataobject,
	conceptDataobject: ConceptDataobject,
	type: string,
	angle: number,
	preventInstantiation: boolean,
	parentInstanceId: string,
	nodes: Node[],
): Node | undefined {
	const sourceNode = nodes.find((n) => n.id === selectedNodeId);
	if (!sourceNode) return;
	if (!graphStore || !domainStore) init();
	let id = templateDataobject.id + "_suggestion";
	if (preventInstantiation) id += "_disabled";
	let node: Node = {
		id: id,
		shape: {
			type: type,
			scale: 0.25,
			url: conceptDataobject?.graphlyTemplate || graphlyTemplatesStore.demoHexagonShape,
		},
		payload: {
			templateDataobjectId: templateDataobject.id,
			parentInstanceId: parentInstanceId,
			...defaultPayload(conceptDataobject),
		},
		satellite: {
			source: selectedNodeId,
			angle: angle,
			distance: (sourceNode.shape.template?.shapeSize ?? 300) * sourceNode.shape.scale * 0.6,
			type: SatelliteType.Hard,
		},
	};
	return node;
}

function satelliteNotificationNode(
	notification: DescriptorNotification,
	angle: number,
	nodes: Node[],
): Node | undefined {
	const sourceNode = nodes.find((n) => n.id === notification.regarding);
	if (!sourceNode) return;
	return {
		id: notification.id,
		shape: {
			type: "satellite-notification",
			scale: 1,
			url: graphlyTemplatesStore.annotationShape,
		},
		payload: {
			label: notification.label,
			description: notification.description,
			icon: notification.icon || "notitia",
			color: notification.color || "#80cbc4",
			collapsed: !instanceWorkspacesStore.expandedNotifications.includes(notification.id),
		},
		spawn: {
			source: notification.regarding,
			angle: angle,
			distance: (sourceNode.shape.template?.shapeSize ?? 300) * sourceNode.shape.scale,
		},
		satellite: {
			source: notification.regarding,
			angle: angle,
			distance: (sourceNode.shape.template?.shapeSize ?? 300) * sourceNode.shape.scale,
			type: SatelliteType.Hard,
		},
	};
}

function getInstantiableDataobjects(
	id: string,
): { templateId: string; parentInstanceId: string; preventInstantiation: boolean }[] {
	if (!graphStore) init();
	let instance = graphStore.instance.dataobject.byId(id);
	if (!instance) return [];
	let template = graphStore.graph.nodes[instance.template] as TemplateDataobject;
	if (!template) return [];
	let instantiable: { templateId: string; parentInstanceId: string; preventInstantiation: boolean }[] = [];
	template.hasA.forEach((hasA) => {
		let hasATemplate = graphStore.graph.nodes[hasA] as TemplateDataobject;
		if (!hasATemplate) return;

		let count = instance?.hasA
			.map((instanceHasAId) => graphStore.instance.dataobject.byId(instanceHasAId))
			?.filter((instanceHasA) => instanceHasA?.template === hasA)?.length;

		// max cardinality not reached
		if (
			hasATemplate.cardinality?.max === -1 ||
			(count != undefined && hasATemplate.cardinality?.max && count < hasATemplate.cardinality?.max)
		) {
			instantiable.push({ templateId: hasA, parentInstanceId: id, preventInstantiation: false });
		}
		// max cardinality reached
		// else instantiable.push({ templateId: hasA, parentInstanceId: id, preventInstantiation: true }); // temporary commented out, disabled state is seen as too distracting by Jan Heuck, better now show them at all
	});
	return instantiable;
}

function position(
	node: InstanceDataobject,
	workspace: InstanceWorkspace,
): {
	x: number | undefined;
	y: number | undefined;
	anchor: Anchor | undefined;
	spawn: Spawn | undefined;
} {
	let pos = instanceWorkspacesStore.nodePositions[workspace.id]?.[node.id] || {};

	// handle new nodes from buttons like RTW
	if (pos?.x === undefined && pos?.y === undefined && otherNodePositionsAreLoaded(workspace.id)) {
		let newPos = instanceWorkspacesStore.newNodePos(node.id); // instanceDataobject.id is equal to node.id so we can use it here
		pos = {
			...pos,
			x: newPos.x,
			y: newPos.y,
		};
		instanceWorkspacesStore.updateNodePositions({
			[node.id]: pos,
		});
	}

	return {
		x: pos?.x || 0,
		y: pos?.y || 0,
		anchor: {
			type: AnchorType.Hard,
			x: pos?.x || 0,
			y: pos?.y || 0,
		},
		spawn: undefined,
	};
}
function otherNodePositionsAreLoaded(workspaceId: string) {
	const nodePositions = instanceWorkspacesStore.nodePositions[workspaceId];
	if (!nodePositions) return false;
	return Object.values(nodePositions).some((pos) => pos.x && pos.y);
}
function scale(node: InstanceDataobject, workspace: InstanceWorkspace, fallback: number = 1): number {
	const scale = instanceWorkspacesStore.nodePositions[workspace.id]?.[node.id]?.scale;
	return scale ? scale : fallback ? fallback : 1;
}

function nodeType(concept: ConceptDataobject): string {
	return concept.graphlyTemplate;
}

function defaultPayload(concept: ConceptDataobject) {
	function processObject(obj: any) {
		if (!symbolsStore) init();
		const result: any = {};
		for (let key in obj) {
			// case default value is set
			if (obj[key] && obj[key].default !== undefined && obj[key].default !== null) {
				let defaultVal = defaultValue(obj[key].default);
				let enumItem = graphStore.descriptor.enumItem.byId(defaultVal);
				if (enumItem) {
					if (enumItem.refersTo === "00000000-0000-0000-0000-000000000101") result[key] = true;
					else if (enumItem.refersTo === "00000000-0000-0000-0000-000000000102") result[key] = false;
					else
						result[key] = symbolsStore.term({
							id: enumItem.refersTo,
							terminology: enumItem.terminology,
							lang: "en",
						});
				} else result[key] = defaultVal;
			}
			// case default value is not set but attribute is set
			else if (obj[key] && !obj[key].default && obj[key].value !== undefined && obj[key].value !== null) {
				let attr = graphStore.graph.nodes[obj[key].value] as ConceptAttribute;
				if (!attr) continue;
				let datatype = graphStore.descriptor.datatype.byId(attr.datatype);
				if (!datatype) continue;
				if (datatype.id === "string") result[key] = "";
			}
			// safeguard for old payloads without value and default value but with arrays
			else if (Array.isArray(obj[key])) {
				continue;
			}
			// case nested object
			else if (typeof obj[key] === "object" && !hasValueAndDefaultKeys(obj[key])) {
				result[key] = processObject(obj[key]);
			}
		}
		return result;
	}
	return processObject(concept.graphlyPayloadMap);
}

function hasValueAndDefaultKeys(obj: any) {
	let keys = Object.keys(obj);
	return keys.includes("value") && keys.includes("default");
}

function defaultValue(_default: any) {
	// if the default value is a terminology object, use it by language preference
	if (isTerminologyObject(_default)) {
		let result = _default[domainStore.languagePreference]?.term?.value || _default["en"]?.term?.value || "";
		return result;
	}
	// else the default value has to be a string
	else {
		return _default;
	}
}
function isTerminologyObject(t: any): boolean {
	function allKeysAreLanguageCodes() {
		for (let key in t) {
			if (!symbolsStore.languages.includes(key as LanguageCode)) {
				// console.log("key is not a language code: ", key);
				return false;
			}
		}
		return true;
	}
	return t && typeof t === "object" && Object.keys(t).length > 0 && allKeysAreLanguageCodes();
}
